import { Trash2 } from "lucide-react";
import { useEffect, useState } from "react";

// Titled-section chrome for the ESTIMATE / SALES ORDER document surfaces.
//
// ONE component, used by the rep's document view (OrderDetailPage → LinesTable)
// and by the CLIENT-facing document (PublicOrderPage). That is deliberate: a
// client and their rep discussing "the Drinkware section" over the phone must be
// looking at the same thing, and the way these two pages drift is by each
// growing its own heading markup.
//
// WHAT THIS IS NOT: it is not the presentation's heading. A presentation is a
// lookbook — 30px chapter markers over 26px product names. An estimate is a
// priced business document whose product names are 15px, and a 30px heading in
// it reads as a different document that has been pasted in. The size here is
// derived from the presentation's OWN ratio (heading ≈ 1.15× the product name
// it groups) applied to this page's type scale, so the two surfaces are
// consistent in RULE rather than in pixels.
//
// The grouping decisions themselves are NOT here — they live in
// @merchd/shared's groupItemsIntoSections, where the offline gate can execute
// them. This file only renders what that function decided.

/** A section title above its group of lines.
 *
 *  `print:break-after-avoid` is the rule that matters commercially: a client
 *  printing an estimate must never get "Drinkware" as the last line of a page
 *  and the drinkware overleaf. That reads as a formatting error in a document
 *  they are being asked to spend money on. */
export function DocumentSectionHeading({
  title,
  first,
  blank,
}: {
  title: string;
  /** The first group needs no top gap — it butts against the column header
   *  strip / page header, which already provides the separation. */
  first?: boolean;
  /** A REAL category the rep never named. CEO 2026-08-07: "simply leave it
   *  blank ... with just a line." Renders the rule and nothing else, at the
   *  same height a titled heading would occupy — a non-breaking space holds the
   *  line box open rather than letting it collapse to a hairline. aria-hidden,
   *  because an empty heading is a visual divider, not a landmark. */
  blank?: boolean;
}) {
  return (
    // h2, not h3. Neither consuming page has an h2 above its line items — the
    // rep page jumps h1 → here, and on the client page the only other h2 sits
    // BELOW the line items, so an h3 here put the document's headings out of
    // order for a screen reader. Styled entirely by className/style, so the
    // level carries no visual weight of its own.
    <h2
      className={
        "font-display border-b-2 border-peach-400 pb-1.5 text-brown-500 " +
        "print:break-after-avoid " +
        (first ? "mb-3" : "mb-3 mt-8")
      }
      style={{ fontSize: "17px", fontWeight: 600, letterSpacing: "0.01em" }}
      {...(blank === true ? { "aria-hidden": "true" as const } : {})}
    >
      {blank === true ? "\u00A0" : title}
    </h2>
  );
}

/** The REP-SIDE editable category header (CEO 2026-08-07).
 *
 *  Same visual weight as DocumentSectionHeading — a rep and their client should
 *  see the same heading, and the only difference here is that it can be typed
 *  into. The title is edited IN PLACE (an input styled as the heading), because
 *  a category is created BLANK and named afterwards: the rep clicks
 *  `+ Category`, gets an empty row with the cursor in it, and types.
 *
 *  NEVER rendered on the client's copy — PublicOrderPage imports the plain
 *  heading only.
 *
 *  The delete affordance says "Remove" rather than carrying a bare bin icon: on
 *  a priced document the rep's real question is "will this delete my products?"
 *  and the answer is no — the products are released back to ungrouped. The
 *  caller reports how many moved. */
export function DocumentSectionEditHeader({
  title,
  itemCount,
  first,
  busy,
  autoFocus,
  onRename,
  onAddProduct,
  onDelete,
}: {
  title: string;
  /** How many products sit under this category. Shown in the confirm, BEFORE
   *  the irreversible click — on a priced document "will this delete my
   *  products?" is the only question the rep actually has. */
  itemCount: number;
  first?: boolean;
  busy?: boolean;
  /** True for a just-created blank category, so the rep can type immediately. */
  autoFocus?: boolean;
  onRename: (title: string) => void;
  onAddProduct: () => void;
  onDelete: () => void;
}) {
  const [draft, setDraft] = useState(title);
  const [confirming, setConfirming] = useState(false);
  // Keep the box in step when the server's value changes underneath (another
  // tab, or a refetch) — but never while the rep is mid-edit in this one.
  const [editing, setEditing] = useState(false);
  useEffect(() => {
    if (!editing) setDraft(title);
  }, [title, editing]);

  const commit = (): void => {
    setEditing(false);
    const next = draft.trim();
    if (next !== title.trim()) onRename(next);
  };

  return (
    <div className={first ? "mb-3" : "mb-3 mt-8"}>
      <div className="flex items-end gap-1 border-b-2 border-peach-400 pb-1.5">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onFocus={() => setEditing(true)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              e.currentTarget.blur();
            }
            if (e.key === "Escape") {
              setDraft(title);
              setEditing(false);
              e.currentTarget.blur();
            }
          }}
          // NOT disabled while this field is being typed into. A native
          // `disabled` force-blurs, and blur COMMITS — so a background mutation
          // finishing mid-keystroke would save a half-typed category name.
          disabled={busy && !editing}
          autoFocus={autoFocus}
          maxLength={120}
          placeholder="Name this category…"
          aria-label="Category name"
          // No focus:outline-none here. The app's global :focus-visible ring is
          // the only thing that tells a keyboard user where they are, and the
          // resting peach rule under the field is NOT a focus state — it is
          // always there.
          className="font-display min-w-0 flex-1 border-0 bg-transparent p-0 text-brown-500 placeholder:font-sans placeholder:text-[15px] placeholder:font-normal placeholder:text-[#8a8a85]"
          style={{ fontSize: "17px", fontWeight: 600, letterSpacing: "0.01em" }}
        />
        {!confirming && (
          <>
            <button
              type="button"
              onClick={onAddProduct}
              disabled={busy}
              className="flex-shrink-0 rounded-md px-2 py-1 text-xs font-semibold text-brown-400 hover:bg-cream-100 hover:text-brown-600 disabled:opacity-50"
            >
              + Add product
            </button>
            <button
              type="button"
              aria-label="Remove category"
              title="Remove this heading. Products under it stay on the quote."
              onClick={() => setConfirming(true)}
              disabled={busy}
              // Divider + padding so the destructive control is not sitting 8px
              // from the positive one — same buffer as the presentation side.
              className="ml-1 flex-shrink-0 rounded-md border-l border-brown-100 p-1 pl-2 text-brown-300 hover:bg-rust-50 hover:text-rust-500 disabled:opacity-50"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </>
        )}
        {confirming && (
          // Replaces the whole control cluster — Add product goes away too, so
          // the row gets simpler at the moment attention should be on one
          // binary decision, and there is no positive action sitting beside the
          // rust one to mis-click. Says the product count BEFORE the click,
          // because that is the only real question.
          <>
            <span className="flex-shrink-0 text-xs text-brown-400">
              {itemCount > 0
                ? `Remove heading? ${itemCount} product${itemCount === 1 ? "" : "s"} stay.`
                : "Remove heading?"}
            </span>
            <button
              type="button"
              onClick={() => {
                setConfirming(false);
                onDelete();
              }}
              className="flex-shrink-0 rounded-md bg-ink px-2 py-1 text-xs font-semibold text-white hover:bg-ink-700"
            >
              Remove
            </button>
            <button
              type="button"
              onClick={() => setConfirming(false)}
              className="flex-shrink-0 rounded-md px-2 py-1 text-xs font-semibold text-brown-400 hover:bg-cream-100"
            >
              Cancel
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/** The caption over an UNHEADED group on a document that HAS sections.
 *
 *  Without it, a run of products sitting above "Drinkware" reads to a client as
 *  "the first category lost its label" rather than "these weren't categorised".
 *  Rendered ONLY when the document has at least one section — an order with no
 *  sections at all must look exactly as it did before this feature existed,
 *  which is what makes every pre-sections estimate safe.
 *
 *  Deliberately a caption and not a chapter head: no rule, no display face, so
 *  it never competes with a real section title. */
export function DocumentSectionCaption({ first }: { first?: boolean }) {
  return (
    <p
      className={
        "text-[10px] font-semibold uppercase text-brown-400 " +
        "print:break-after-avoid " +
        (first ? "mb-2" : "mb-2 mt-8")
      }
      // Matches the micro-label convention the CLIENT page already uses for
      // AddressBlock and MetaRow — 10px / 600 / 0.08em / brown-400. Two
      // different treatments of the same visual role a few inches apart on one
      // document is a defect, not a variation. (Tailwind's tracking-wide is
      // 0.025em, which is what this used to be.)
      style={{ letterSpacing: "0.08em" }}
    >
      Also included
    </p>
  );
}

/** The table-view equivalent: a full-width band row.
 *
 *  A <table> cannot take a floating heading between its rows, so the grouping
 *  becomes a spanning band — the standard grouped-table pattern, and it keeps
 *  the column grid unbroken. Same words, same accent, sized down because table
 *  rows are far denser than document cards.
 *
 *  `title` null renders the "Also included" band instead of a heading, so the
 *  two views agree about every group the shared grouping produced.
 *
 *  REP-ONLY. The table view lives on OrderDetailPage, which loads no print
 *  stylesheet and carries no print: classes at all — only the four public
 *  client-facing pages do. The print class below is therefore inert today and
 *  is kept for the day that changes; the CLIENT-facing surfaces use
 *  DocumentSectionHeading, a block element, which is the well-supported
 *  fragmentation case and the one already shipping on presentations. */
export function DocumentSectionBandRow({
  title,
  colSpan,
  first,
  blank,
}: {
  title: string | null;
  /** A real but unnamed category — a band with no words, matching the blank
   *  heading in the document view. Without this it would read "Also included",
   *  which is the label for products in NO category at all. */
  blank?: boolean;
  colSpan: number;
  /** The first band sits directly beneath the column-header row, which is ALSO
   *  cream. Left identical, the two stack into one oversized header block and
   *  the group title reads as another column label. So the first band drops the
   *  fill and moves its rule BELOW the text — which is where
   *  DocumentSectionHeading puts its rule anyway, so the two views agree. */
  first?: boolean;
}) {
  return (
    <tr className="print:break-after-avoid">
      <td
        colSpan={colSpan}
        className={
          "px-4 py-2 " +
          (first
            ? "border-b-2 border-peach-400 bg-white "
            : "border-t-2 border-peach-400 bg-cream-50 ") +
          // `title` is null for BOTH the caption and a blank heading, so keying
          // the type off it alone gave an unnamed category the CAPTION's 10px
          // uppercase treatment — a visibly shorter row than its titled
          // siblings, reading as a squashed or broken row rather than a divider
          // of equal weight. A blank heading is a HEADING; only the words are
          // missing.
          (title === null && blank !== true
            ? "text-[10px] font-semibold uppercase text-brown-400"
            : "font-display text-[13px] font-semibold text-brown-500")
        }
        style={
          title === null && blank !== true
            ? { letterSpacing: "0.08em" }
            : undefined
        }
      >
        {blank === true ? "\u00A0" : (title ?? "Also included")}
      </td>
    </tr>
  );
}
